<template>
  <div>
    <div class="bg-blue-900 p-4 lg:p-12">
      <div class="max-w-5xl mx-auto mb-12">
        <div class="flex flex-col md:flex-row md:items-center">
          <h3 class="text-white font-bold">Edit Offer</h3>
        </div>
      </div>
    </div>
    <div class="px-4 pb-4 lg:px-12 lg:pb-12 -mt-12">
      <div class="max-w-5xl mx-auto">
        <div class="space-y-16">
          <div class="rounded bg-white shadow-lg p-12">
            <OfferForm
              :default-data="defaultData"
              :default-lines="defaultLines"
              :recipient-options="recipientOptions"
              :issuer-options="issuerOptions"
              :disabled="isLoading"
              :server-errors="serverErrors"
              @offer-form:submit="doUpdate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { omit } from 'lodash-es';
import { mapActions } from 'vuex';
import { offersClient } from '@/api/clients';
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import OfferFormVue from '@/components/Offers/OfferForm.vue';

const STATUS_IDLE = 'idle';
const STATUS_PENDING = 'pending';
const STATUS_RESOLVED = 'resolved';
const STATUS_REJECTED = 'rejected';

export default {
  components: {
    OfferForm: OfferFormVue,
  },

  props: {
    accountId: {
      type: String,
      required: true,
    },
    offerUuid: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      status: STATUS_IDLE,
      serverErrors: {},
      errorMessage: '',
      defaultData: {
        recipientName: '',
        recipientAddress: '',
        recipientVatNumber: '',
        recipientVatOffice: '',
      },
      defaultLines: [],
      recipientOptions: [],
      issuerOptions: [],
    };
  },

  computed: {
    isLoading() {
      return this.status === STATUS_PENDING;
    },
    isResolved() {
      return this.status === STATUS_RESOLVED;
    },
    isRejected() {
      return this.status === STATUS_REJECTED;
    },
  },

  async beforeRouteEnter(to, from, next) {
    try {
      const response = await offersClient.edit(to.params.offerUuid);

      if (!response) next(false);
      if (response.status !== 200) next(false);
      if (response?.headers['content-type'] !== 'application/json') next(false);

      next((vm) => {
        vm.setData(response.data);
      });
    } catch (error) {
      next(false);
    }
  },

  methods: {
    ...mapActions('ui/offers', {
      updateOffer: 'update',
    }),

    setData({ data, issuers }) {
      const { accountRelation, offerLines, ...restData } = data;

      const { related_account: relatedAccount } = accountRelation;

      this.defaultData = { ...this.defaultData, ...omit(restData, ['can', 'account']) };
      this.defaultLines = [...offerLines];

      this.recipientOptions.push({
        label: relatedAccount.name,
        value: relatedAccount.id,
        registrations: relatedAccount.registrations || [],
      });

      this.issuerOptions = issuers.map((item) => ({
        label: item.name,
        value: item.id,
        registrations: item.registrations || [],
      }));
    },

    async doUpdate(formData) {
      if (this.isLoading) return;

      const { offerUuid } = this;

      this.errorMessage = '';
      this.status = STATUS_PENDING;

      try {
        await this.updateOffer({ offerUuid, formData });

        this.status = STATUS_RESOLVED;
      } catch (error) {
        this.status = STATUS_REJECTED;

        if (!error.response) {
          this.errorMessage = 'Something went wrong. Try again later';
        } else if (error.response.status === HTTP_UNPROCESSABLE_ENTITY) {
          this.serverErrors = error.response.data.errors;
          this.errorMessage = error.response.data.message;
        }
      }
    },
  },
};
</script>
